import React, { useState, useContext, useEffect, useRef } from "react";
import "./styles/styles.css";
import "../../styles/global.css";
import { images, icons } from "../../constants";
import getRoutes from "../../routes/routes";
import CustomLink from "./components/CustomLink";
import BellNotify from "./components/BellNotify";
import Themes from "./components/Themes";
import Languages from "./components/Languages";
import { useNavigate } from "react-router-dom";
import Modal from "../Modal/Modal";
import { TranslationsContext } from "../../utils/TranslationsContext";
import { useViewer } from "../../utils/ViewerContext";
import { handleLogout } from "../../utils/methods";

// Sidebar expanded/collapsed preference. New key: the old "expandableNavbar"
// got "false" written on every load (and on any narrow window), so it never
// reflected an actual choice — this one is only written when the user
// toggles on desktop, and missing means expanded.
const SIDEBAR_EXPANDED_KEY = "sidebarExpanded";

const NavBar = ({ setLanguage, setNavWidth, scan }) => {
  const [expandableNavbar, setExpandableNavbar] = useState(() => {
    // Expanded by default (no saved choice yet); a collapse the user made
    // is remembered. Desktop only — narrower screens use the top bar.
    const saved = localStorage.getItem(SIDEBAR_EXPANDED_KEY);
    return saved !== "false" && window.innerWidth >= 1000;
  });
  const { t } = useContext(TranslationsContext);
  const [openLanguages, setOpenLanguages] = useState(false);
  const navRef = useRef();
  const [theme, setTheme] = useState(
    () => localStorage.getItem("theme") || "light",
  );
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  const [isBarOpen, setIsBarOpen] = useState(false);
  const [isToggleHovered, setIsToggleHovered] = useState(false);

  useEffect(() => {
    const updateNavWidth = () => {
      const screenWidth = window.innerWidth;

      if (screenWidth < 1000) {
        setNavWidth(0);
      } else if (navRef.current) {
        requestAnimationFrame(() => {
          if (navRef.current) {
            setNavWidth(navRef.current.offsetWidth);
          }
        });
      }
    };

    updateNavWidth();
    window.addEventListener("resize", updateNavWidth);

    return () => {
      window.removeEventListener("resize", updateNavWidth);
    };
  }, [setNavWidth, expandableNavbar]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1000) {
        setExpandableNavbar(false);
      } else {
        // Back on desktop: return to the saved preference (default expanded).
        setExpandableNavbar(
          localStorage.getItem(SIDEBAR_EXPANDED_KEY) !== "false",
        );
      }
    };

    // Poziv na mount
    handleResize();

    // Listener za resize
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Save to localStorage whenever expandableNavbar changes (only on screens >= 1000px)
    if (window.innerWidth >= 1000) {
      localStorage.setItem(SIDEBAR_EXPANDED_KEY, expandableNavbar.toString());
    }
  }, [expandableNavbar]);

  const navigate = useNavigate();
  const handleLogoutClick = async () => {
    await handleLogout();
  };

  const { role } = useViewer();
  const routes = getRoutes(t, role);
  return (
    <div
      className={`flex-direction-column items-center justify-center ${
        expandableNavbar ? "expandable" : ""
      } navbar`}
      ref={navRef}
    >
      <div className="flex-direction-row items-center width-100 navbar-logo-hamburger">
        <img src={images.emblemMenu} className="navbar-logo-img" alt="" />
        <div
          className={`navbar-toggle-tab ${expandableNavbar ? "expanded" : ""}`}
          onClick={() => {
            if (window.innerWidth >= 1000) {
              setExpandableNavbar(!expandableNavbar);
            }
          }}
          onMouseEnter={() => setIsToggleHovered(true)}
          onMouseLeave={() => setIsToggleHovered(false)}
        >
          <img
            src={
              isToggleHovered
                ? expandableNavbar
                  ? icons.menu_arrow_left_blue
                  : icons.menu_arrow_right_blue
                : theme === "dark"
                  ? expandableNavbar
                    ? icons.menu_arrow_left_dark
                    : icons.menu_arrow_right_dark
                  : expandableNavbar
                    ? icons.menu_arrow_left
                    : icons.menu_arrow_right
            }
            className="navbar-toggle-arrow"
            alt="toggle menu"
          />
        </div>
        <div className="flex-direction-row items-center gap-18 language-hamburger">
          <div className="flex-direction-row items-center gap-2">
            <div className="navbar-themes-under-999">
              <Themes
                setIsBarOpen={(value) => setIsBarOpen(value)}
                changeThemeInNavbar={(theme) => {
                  setTheme(theme);
                }}
              />
            </div>{" "}
            <div
              className="navbar-languages-under-999"
              // onClick={() => setOpenLanguages(!openLanguages)}
            >
              <Languages
                openLanguages={openLanguages}
                setIsBarOpen={(value) => setIsBarOpen(value)}
                setLanguageGeneral={setLanguage}
                setOpenLanguages={setOpenLanguages}
                small={true}
              />
            </div>
          </div>

          <img
            className="hamburger"
            src={isBarOpen ? icons.close : icons.hamburger}
            onClick={() => setIsBarOpen(!isBarOpen)}
            width={30}
            height={30}
            alt="X"
          />
        </div>
      </div>
      <div
        className={`flex-direction-column gap-96 justify-between width-100 height-100 navbar-menu ${
          isBarOpen ? "open" : ""
        }`}
      >
        <div className="flex-direction-column  navbar-links">
          {!scan &&
            routes.map((route, index) => (
              <CustomLink
                key={index}
                to={route.to}
                src={
                  theme === "light" ? icons[route.icon] : icons[route.iconDark]
                }
                onLinkClick={() => setIsBarOpen(false)}
              >
                {route.title}
              </CustomLink>
            ))}
        </div>
        <div className="flex-direction-column navbar-settings">
          {process.env.REACT_APP_MODE !== "first" && (
            <BellNotify onItemClick={() => setIsBarOpen(false)} />
          )}
          <div className="navbar-themes">
            {" "}
            <Themes
              setIsBarOpen={(value) => setIsBarOpen(value)}
              changeThemeInNavbar={(theme) => {
                setTheme(theme);
              }}
            />
          </div>

          <div className="navbar-languages">
            <Languages
              openLanguages={openLanguages}
              setOpenLanguages={setOpenLanguages}
              setIsBarOpen={(value) => setIsBarOpen(value)}
              setLanguageGeneral={setLanguage}
              expanded={expandableNavbar}
            />
          </div>
          <div
            className="flex-directions-row items-center pointer navbar-link"
            onClick={() => {
              setIsBarOpen(false);
              setIsProfileModalOpen(true);
            }}
          >
            <img
              src={theme == "light" ? icons.profile : icons.profile_dark}
              width={24}
              height={24}
              alt="prof"
            />
            <p className="inria-sans-300 font-14 weight-700 navbar-term">
              {t("PROFILE")}
            </p>
          </div>
          <div
            className="flex-directions-row items-center pointer navbar-link"
            onClick={handleLogoutClick}
          >
            <img
              src={theme == "light" ? icons.logout : icons.logout_dark}
              width={24}
              height={24}
              alt="log"
            />
            <p className="inria-sans-300 font-14 weight-700 navbar-term">
              {t("LOGOUT")}
            </p>
          </div>
        </div>
      </div>
      {isProfileModalOpen && (
        <Modal
          type="profile"
          onCloseClick={() => setIsProfileModalOpen(false)}
        />
      )}
    </div>
  );
};

export default NavBar;
